import React from 'react';
import { cn } from '../../../lib/cn';
import { useFieldControl } from './Field';

/**
 * Radio, `.dsk-radio`.
 *
 * Source selector: `system.css` `.dsk-radio`.
 *
 * Doctrine enforced:
 * - Selection is STATE, so the checked ring is ACCENT and never ink.
 * - Checked is drawn by thickening the border to 5px rather than by a
 *   pseudo-element dot: there is nothing that can misalign.
 * - A real `<input type="radio">`, so a shared `name` gives arrow-key roving,
 *   grouping and form submission with no JavaScript.
 * - The control stays 18px; the hit target is the row around it (`OptionRow`).
 */
export const Radio = React.forwardRef(function Radio({ className, children, ...rest }, ref) {
  // Dropped on purpose, see the note in Switch.jsx: this is a bare <input>, a
  // void element, and children reaching it through the spread throws and takes
  // down the whole tree. Use <OptionRow> or a <Label> for the text.
  if (process.env.NODE_ENV !== 'production' && children != null) {
    // eslint-disable-next-line no-console
    console.warn('Radio does not render children. Use <OptionRow> or pair it with a <Label>.');
  }

  const field = useFieldControl(rest);

  return (
    <input
      ref={ref}
      type="radio"
      className={cn('dsk-radio', className)}
      {...rest}
      id={field.id}
      aria-describedby={field['aria-describedby']}
    />
  );
});
Radio.displayName = 'Radio';

export default Radio;
